import type { FC, ReactNode } from 'react';
import React, { Fragment, memo, useCallback, useContext, useEffect, useState, useRef } from 'react';
import { css } from '@compiled/react';
// eslint-disable-next-line no-restricted-imports
import { CommentsSectionLoader } from '@confluence-classic/confluence-frontend-server/src/components/CommentsSection';
import { flushSync } from 'react-dom';

import { token } from '@atlaskit/tokens';

import { useQuery } from '@atlassian/ufo-apollo-log/use-query';

import { useIsReactionsEnabled } from '@confluence/comments-hooks';
import { GeneralShortcutListener, UNIVERSAL_OPEN_COMMENT_EDITOR } from '@confluence/shortcuts';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { useRouteActions } from '@confluence/route-manager';
import { DEFAULT_LIMIT } from '@confluence/page-comments-queries';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import {
	VIEW_PAGE_COMMENTS_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { LoadingPriority } from '@confluence/loadable';
import { getCommentPositionTopOfSection } from '@confluence/comments-util';
import { CommentPlaceholder } from '@confluence/comment-simple/entry-points/CommentPlaceholder';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';
import { CommentsSectionLoader as CommentsSectionLoaderWrapper } from '@confluence/comments-section';
import { Attribution, withErrorBoundary } from '@confluence/error-boundary';
import {
	CommentsSectionQuery,
	CommentsSectionWithoutReactionsQuery,
} from '@confluence/page-comments-queries/entry-points/CommentsSectionQuery.graphql';
import { markErrorAsHandled } from '@confluence/graphql';
import type {
	CommentsSectionQuery as CommentsSectionQueryType,
	CommentsSectionWithoutReactionsQuery as CommentsSectionWithoutReactionsQueryType,
	CommentsSectionQueryVariables,
} from '@confluence/page-comments-queries/entry-points/__types__/CommentsSectionQuery';
import { fg } from '@confluence/feature-gating';

const loadingPageCommentWrapperStyles = css({
	display: 'flex',
	width: 'auto',
	justifyContent: 'center',
	padding: token('space.200'),
	paddingBottom: token('space.250'),
	position: 'absolute',
	bottom: '0px',
	zIndex: 101,
});

const pageCommentWrapperStyles = css({
	position: 'relative',
	zIndex: 101, // The highest z-index this intersects with is the byline, whose current index is 100 (thus, we use 101)
	display: 'flex',
	justifyContent: 'center',
});

const pageCommentStyle = css({
	clear: 'both',
	width: '100%',
	flexDirection: 'column',
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'flex-start',
	overflow: 'visible',
	maxWidth: 'unset',
	margin: 'unset',
	minWidth: '68%',
});

const pageCommentWiderStyles = css({
	width: '80%',
	margin: '0 auto',
	maxWidth: '940px',
	minWidth: '580px',
});

const pageCommentAbsoluteWrapperStyles = css({
	position: 'absolute',
	left: 0,
	width: 'calc(100% - 56px)',
});

const PageCommentAbsoluteWrapper = ({ children }: { children: ReactNode }) => (
	<div data-testId="page-comment-absolute-wrapper" css={[pageCommentAbsoluteWrapperStyles]}>
		{children}
	</div>
);

const sendKeyboardShortcutAnalytics = ({
	source,
	shortcut,
}: {
	source: string;
	shortcut: string;
}) => {
	let pageMode: string;
	if (source === PageMode.VIEW) {
		pageMode = 'viewPage';
	} else if (source === PageMode.LIVE) {
		pageMode = 'livePages';
	}
	void getAnalyticsWebClient().then((analyticsClient) => {
		analyticsClient.sendUIEvent({
			source: pageMode,
			action: 'pressed',
			actionSubject: 'keyboardShortcut',
			actionSubjectId: 'createComment',
			attributes: {
				key: shortcut,
			},
		});
	});
};

const LazyLoadablePageComments = ({
	children,
	shouldLazyLoad,
	renderPlaceholder = undefined,
	loadPageCommentsCallback = undefined,
	isCommentsReady,
}: {
	children: React.ReactElement;
	shouldLazyLoad?: boolean;
	renderPlaceholder?: () => React.ReactElement;
	loadPageCommentsCallback?: () => void;
	isCommentsReady: boolean;
}) => {
	const pageMode = useGetPageMode();
	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const observerRef = useRef<IntersectionObserver | null>(null);
	const [commentSectionTop, setCommentSectionTop] = useState<number | undefined>(undefined);

	// load comments immediately if not lazy load, otherwise wait until it is in the viewport
	const [loadPageComments, setLoadPageComments] = useState(!shouldLazyLoad);

	const { setQueryParams } = useRouteActions();

	useEffect(() => {
		const editorTextArea = document.getElementById('ak-editor-textarea');

		if (editorTextArea) {
			// Create a ResizeObserver to watch for size changes
			const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
				if (fg('confluence_footer_comment_styling_change')) {
					const newTop = getCommentPositionTopOfSection(entries);
					// Warning temporary to unblock modernisation release (see COMMENTS-3668)
					if (fg('confluence_footer_comment_styling_fix_flicker')) {
						flushSync(() => {
							newTop && setCommentSectionTop(newTop);
						});
					} else {
						newTop && setCommentSectionTop(newTop);
					}
				}
			});
			// Start observing the editorTextArea and documentContent for size changes
			resizeObserver.observe(editorTextArea);
			// Cleanup function to disconnect the observer on unmount
			return () => resizeObserver.disconnect();
		}
	}, []);

	useEffect(() => {
		// viewport intersection detection for SPA only
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (!process.env.REACT_SSR) {
			// eslint-disable-next-line no-restricted-syntax
			observerRef.current = new IntersectionObserver(([entry]) => {
				if (entry.isIntersecting) {
					// show page comments when the wrapper is in viewport
					setLoadPageComments(true);
					observerRef.current?.disconnect();
				}
			});

			// Remove the observer as soon as the component is unmounted
			return () => {
				observerRef.current?.disconnect();
			};
		}
		// NOOP for SSR
	}, []);

	useEffect(() => {
		// viewport intersection detection for SPA only
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (!process.env.REACT_SSR) {
			if (wrapperRef.current && !loadPageComments) {
				// attach intersection observer after content is ready
				observerRef.current?.observe(wrapperRef.current);
			}
		}
	}, [loadPageComments]);

	useEffect(() => {
		if (loadPageComments) {
			loadPageCommentsCallback?.();
		}
	}, [loadPageComments, loadPageCommentsCallback]);

	// render placeholder only in SSR
	const shouldRenderPageComments =
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		!process.env.REACT_SSR && loadPageComments;

	const PositioningWrapper = fg('confluence_frontend_general_comments_styles')
		? PageCommentAbsoluteWrapper
		: Fragment;

	return (
		<Fragment>
			{fg('confluence_frontend_general_comments_styles') ? (
				<div css={[!isCommentsReady && loadingPageCommentWrapperStyles]} ref={wrapperRef} />
			) : null}
			<PositioningWrapper>
				<div
					data-testId="page-comment-wrapper"
					{...(fg('confluence_frontend_general_comments_styles') ? {} : { ref: wrapperRef })}
					css={[
						fg('confluence_frontend_general_comments_styles') && pageCommentWiderStyles,
						isCommentsReady ? pageCommentWrapperStyles : loadingPageCommentWrapperStyles,
					]}
					style={{
						visibility: isCommentsReady ? 'visible' : 'hidden',
						top: isCommentsReady ? commentSectionTop : undefined,
					}}
				>
					<GeneralShortcutListener
						accelerator={UNIVERSAL_OPEN_COMMENT_EDITOR}
						listener={(e) => {
							e.preventDefault();
							if (document.querySelector('[data-testid="editor-floating-toolbar"]')) {
								return;
							}
							if (!isCommentsReady) {
								setQueryParams({
									showCommentArea: 'true',
								});
								//This will scroll to and load the page comments section if its not available (e.g. on long pages)
								wrapperRef.current?.scrollIntoView &&
									wrapperRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
							} else {
								window.dispatchEvent(new Event('openPageCommentEditor'));
							}
							sendKeyboardShortcutAnalytics({
								source: pageMode,
								shortcut: UNIVERSAL_OPEN_COMMENT_EDITOR,
							});
						}}
					/>
					<div css={pageCommentStyle}>
						{shouldRenderPageComments ? children : renderPlaceholder?.()}
					</div>
				</div>
			</PositioningWrapper>
		</Fragment>
	);
};

const PageFooterCommentsComponent: FC<{
	contentId: string;
	loadingPriority: LoadingPriority | null;
}> = memo(({ contentId, loadingPriority }) => {
	/* Preload CommentsSectionQuery - We preload CommentsSectionQuery as part of next/packages/query-preloaders/src/index.ts,
    but its bestEffort Preloading - meaning if it exceeds the threshold it wont wait for query to finish
    Adding preloading here as well to ensure data is ready once user scrolls*/
	const isReactionsEnabled = useIsReactionsEnabled(contentId);
	const { error } = useQuery<
		CommentsSectionQueryType | CommentsSectionWithoutReactionsQueryType,
		CommentsSectionQueryVariables
	>(isReactionsEnabled ? CommentsSectionQuery : CommentsSectionWithoutReactionsQuery, {
		variables: {
			contentId,
			offset: '',
			first: DEFAULT_LIMIT,
		},
		fetchPolicy: 'cache-and-network',
		skip: !contentId,
	});

	/*  Marking all graphql errors here as handled since we are only preloading query. The CommentsSectionFabric component
    where the query data is used has exhaustive error handling*/

	if (error) {
		markErrorAsHandled(error);
	}

	const isLivePage = useIsCurrentPageLive();
	const { getQueryParams } = useContext(RoutesContext);
	const experienceTracker = useContext(ExperienceTrackerContext);

	const { focusedCommentId, replyToComment, showCommentArea, editComment, commentId } =
		getQueryParams();

	/* check for query params - If anyone of these are true just load Page comments */
	const isFocusedComment = Boolean(
		focusedCommentId || replyToComment || showCommentArea || (editComment === 'true' && commentId),
	);

	const [isCommentsReady, setIsCommentsReady] = useState(false);

	const startExperienceTracker = useCallback(() => {
		experienceTracker.start({
			name: VIEW_PAGE_COMMENTS_EXPERIENCE,
			attributes: {
				isConditionalRenderingOn: true,
				isFocusedComment,
				editor: 'FABRIC',
				contentId,
			},
		});
	}, [experienceTracker, isFocusedComment, contentId]);

	// Display comment Placeholder
	const renderCommentPlaceholder = () => {
		return <CommentPlaceholder showPlaceholder />;
	};

	const windowHeight = window.innerHeight;
	const editorProseMirrorHeight =
		document.querySelector('.ak-editor-content-area .ProseMirror')?.scrollHeight || 0;
	const contentHeight = editorProseMirrorHeight / windowHeight;

	// The placeholder isn't in view if the content takes up more than 30% and less than 2% of the screen
	const shouldRenderPlaceholder = contentHeight < 0.3 && contentHeight > 0.02;

	return (
		<LazyLoadablePageComments
			shouldLazyLoad={!isFocusedComment}
			renderPlaceholder={
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				process.env.REACT_SSR || shouldRenderPlaceholder ? renderCommentPlaceholder : undefined
			}
			loadPageCommentsCallback={startExperienceTracker}
			isCommentsReady={isCommentsReady}
		>
			<Fragment>
				{
					// Important! Here, the renderCommentPlaceholder() must match the renderPlaceholder
					// in LazyLoadPageComments to ensure the initial hydration matches the SSR render result
					!isCommentsReady && !isLivePage && renderCommentPlaceholder()
				}
				<CommentsSectionLoaderWrapper
					contentId={contentId}
					classicComments={CommentsSectionLoader}
					handlePageCommentsRender={() => setIsCommentsReady(true)}
					loadingPriority={loadingPriority || LoadingPriority.PAINT}
				/>
			</Fragment>
		</LazyLoadablePageComments>
	);
});

export const PageFooterComments = withErrorBoundary({
	attribution: Attribution.COLLABORATION,
})(PageFooterCommentsComponent);
